import React, { useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import * as XLSX from "xlsx";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "65%",
  maxHeight: "80vh",
  overflowY: "auto",
  backgroundColor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 3,
};

export default function TapperThreadRingExcelUploadModal({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [acceptedRows, setAcceptedRows] = useState([]);
  const [rejectedRows, setRejectedRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setAcceptedRows([]);
    setRejectedRows([]);
  };

  const parseNumber = (val) => {
    if (val === null || val === undefined || val === "" || val === "-") return null;
    if (typeof val === "number") return val;
    const cleaned = String(val).replace(/[^0-9.-]/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileReader = new FileReader();

    fileReader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (!rows || rows.length === 0) {
          toast.error("Excel file has no data");
          return;
        }

        let headerRowIndex = -1;
        for (let i = 0; i < Math.min(10, rows.length); i++) {
          const rowStr = (rows[i] || []).join(" ").toLowerCase();
          if (
            rowStr.includes("std") ||
            rowStr.includes("pitch") ||
            rowStr.includes("step") ||
            rowStr.includes("type")
          ) {
            headerRowIndex = i;
            break;
          }
        }

        if (headerRowIndex === -1) headerRowIndex = 0;

        const headers = rows[headerRowIndex] || [];
        const colMap = {};

        headers.forEach((h, idx) => {
          if (!h) return;
          const cleanH = String(h).toLowerCase().trim();

          if (
            cleanH.includes("pitch dia max") ||
            cleanH.includes("pitch dial max")
          ) {
            colMap.pitchDialMax = idx;
          } else if (
            cleanH.includes("pitch dia min") ||
            cleanH.includes("pitch dial min")
          ) {
            colMap.pitchDialMin = idx;
          } else if (cleanH.includes("max step")) {
            colMap.maxStep = idx;
          } else if (cleanH.includes("basic step")) {
            colMap.basicStep = idx;
          } else if (cleanH.includes("min step")) {
            colMap.minStep = idx;
          } else if (cleanH.includes("total length")) {
            colMap.totalLength = idx;
          } else if (cleanH.includes("std") || cleanH.includes("speci")) {
            colMap.specification = idx;
          } else if (cleanH.includes("type")) {
            colMap.gaugeType = idx;
          }
        });

        const accepted = [];
        const rejected = [];

        for (let i = headerRowIndex + 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length === 0) continue;

          const hasAnyValue = row.some(
            (val) => val !== null && val !== undefined && String(val).trim() !== ""
          );
          if (!hasAnyValue) continue;

          const rawSpec =
            colMap.specification !== undefined ? row[colMap.specification] : "";
          const specStr =
            rawSpec !== null && rawSpec !== undefined ? String(rawSpec).trim() : "";

          const rawType =
            colMap.gaugeType !== undefined ? row[colMap.gaugeType] : "";
          const typeStr = String(rawType || "").trim().toLowerCase();

          let resolvedGaugeType = null;
          if (
            typeStr.includes("plane") ||
            typeStr.includes("guage plane") ||
            typeStr.includes("gauge plane")
          ) {
            resolvedGaugeType = 1;
          } else if (typeStr.includes("small") || typeStr.includes("end")) {
            resolvedGaugeType = 2;
          } else if (typeStr === "1" || Number(rawType) === 1) {
            resolvedGaugeType = 1;
          } else if (typeStr === "2" || Number(rawType) === 2) {
            resolvedGaugeType = 2;
          } else if (!typeStr) {
            resolvedGaugeType = 1;
          }

          const rawTotalLen =
            colMap.totalLength !== undefined ? row[colMap.totalLength] : "";
          const totalLenStr =
            rawTotalLen !== null && rawTotalLen !== undefined
              ? String(rawTotalLen).trim()
              : "";

          const parsedRow = {
            specification: specStr || "-",
            gaugeType: resolvedGaugeType,
            rawGaugeType: String(rawType || "N/A"),
            gaugeTypeLabel:
              resolvedGaugeType === 1
                ? "Gauge Plane"
                : resolvedGaugeType === 2
                ? "Small End"
                : String(rawType || "Invalid"),
            pitchDialMax: parseNumber(
              colMap.pitchDialMax !== undefined ? row[colMap.pitchDialMax] : null
            ),
            pitchDialMin: parseNumber(
              colMap.pitchDialMin !== undefined ? row[colMap.pitchDialMin] : null
            ),
            maxStep: parseNumber(
              colMap.maxStep !== undefined ? row[colMap.maxStep] : null
            ),
            basicStep: parseNumber(
              colMap.basicStep !== undefined ? row[colMap.basicStep] : null
            ),
            minStep: parseNumber(
              colMap.minStep !== undefined ? row[colMap.minStep] : null
            ),
            totalLength: totalLenStr || null,
          };

          if (!resolvedGaugeType) {
            rejected.push({
              ...parsedRow,
              rejectionReason: `Invalid Gauge Type for Taper Thread Ring (found: '${rawType}')`,
            });
          } else if (!specStr || specStr === "-") {
            rejected.push({
              ...parsedRow,
              rejectionReason: "Missing Specification",
            });
          } else {
            accepted.push(parsedRow);
          }
        }

        setAcceptedRows(accepted);
        setRejectedRows(rejected);
      } catch (err) {
        console.error(err);
        toast.error("Invalid Excel file");
      }
    };

    fileReader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleContinue = async () => {
    if (acceptedRows.length === 0) return;
    setSubmitting(true);

    let successCount = 0;

    for (let i = acceptedRows.length - 1; i >= 0; i--) {
      const row = acceptedRows[i];
      const payload = {
        gaugeType: row.gaugeType,
        specification: row.specification,
        pitchDialMax: row.pitchDialMax,
        pitchDialMin: row.pitchDialMin,
        maxStep: row.maxStep,
        basicStep: row.basicStep,
        minStep: row.minStep,
        totalLength: row.totalLength,
      };

      try {
        await axiosWithToken.post(`${BASE_URL}taperThreadSizes`, payload);
        successCount++;
      } catch (err) {
        console.error("Error importing row:", err);
      }
    }

    setSubmitting(false);
    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} record(s)!`);
      handleClose();
      if (onSuccess) onSuccess();
    } else {
      toast.error("Failed to upload specifications.");
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={handleOpen}
      >
        Upload Excel
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Typography variant="h6" textAlign="center" mb={2}>
            Upload Taper Thread Ring Excel
          </Typography>

          <Typography textAlign="right" mb={2} color="text.secondary">
            Rejected: {rejectedRows.length} | Accepted: {acceptedRows.length}
          </Typography>

          <Button variant="contained" color="primary" component="label" sx={{ mb: 2 }}>
            Select Excel File
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
            />
          </Button>

          {acceptedRows.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 260, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Gauge Type</TableCell>
                    <TableCell>Specification</TableCell>
                    <TableCell align="center">Pitch Dial Max</TableCell>
                    <TableCell align="center">Pitch Dial Min</TableCell>
                    <TableCell align="center">Basic Step</TableCell>
                    <TableCell align="center">Total Length</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {acceptedRows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.gaugeTypeLabel}</TableCell>
                      <TableCell>{row.specification}</TableCell>
                      <TableCell align="center">{row.pitchDialMax ?? "-"}</TableCell>
                      <TableCell align="center">{row.pitchDialMin ?? "-"}</TableCell>
                      <TableCell align="center">{row.basicStep ?? "-"}</TableCell>
                      <TableCell align="center">{row.totalLength ?? "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {rejectedRows.length > 0 && (
            <>
              <Typography fontWeight="bold" color="error" sx={{ mb: 1, mt: 1 }}>
                Rejected Rows ({rejectedRows.length})
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Gauge Type in File</TableCell>
                      <TableCell>Specification</TableCell>
                      <TableCell sx={{ color: "#d32f2f" }}>Rejection Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rejectedRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{row.rawGaugeType}</TableCell>
                        <TableCell>{row.specification}</TableCell>
                        <TableCell sx={{ color: "#d32f2f" }}>{row.rejectionReason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Box mt={3} display="flex" justifyContent="space-between">
            <Button variant="contained" color="error" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!acceptedRows.length || submitting}
              onClick={handleContinue}
            >
              {submitting ? "Uploading..." : "Continue"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
